import { useEffect, useRef, useState } from 'react'
import './badges-landing.styles.scss';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import BadgesList from './badges-list.component';
import BadgesForm from './badges-form.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import { membershipRestrictions, testMemberRestrictions } from '@/utils/membershipRestrictions';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import { properCase } from '@/utils/properCase';
import { getCollectionFromDb } from '@/utils/getDataFromDb';
import { completeGameElementData } from '@/components/PREVIEW/previewGameElements';
import { useEffectiveMembership } from '@/utils/useEffectiveMembership';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';

const BadgesLandingPage = ({
  gameData,
  gameStats,
  gameElements,
  role
}) => {

  const membership = useEffectiveMembership();
  const [showUpgradeModal, setShowUpgradeModal] = useState(null);
  const [badgesFromDb, setBadgesFromDb] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const elementRef = useRef();
  const elementListenerRef = useRef();

  const gameId = gameData?.gameId;
  const isPreview = location.pathname.includes('/preview/manager/');

  useEffect(() => {
    if (!gameData || !membership) return;
    checkMembership();
    function checkMembership() {
      const element = 'badges'
      const res = testMemberRestrictions({ 'gameData': gameData, 'membership': membership, 'element': element })
      // console.log(res);
      if (res.disabled && membershipRestrictions) {
        let message = ''
        const planRest = membershipRestrictions[membership.membership];
        if (res.disabledReason.includes(element)) {
          message = `While using the ${planRest.title}, you are not able to access ${properCase(element)}.`;
        }
        setShowUpgradeModal(message)
      }
    }
  }, [gameData, membership])

  useEffect(() => {
    if (!gameData?.gameName) return;
    changeDocumentTitle(`Badges | Manage ${gameData.gameName ?? 'Game'}`);
  }, [gameData?.gameName])

  useEffect(() => {
    if (!gameId) return;
    function getGameElementsToManage() {
      if (isPreview) {
        const badgeObj = {};
        if (!completeGameElementData) return;
        Object.values(completeGameElementData)
          .filter(e => e.type === 'badge' && e.status === 'active')
          .forEach(e => {
            badgeObj[e.id] = e;
          });
        setBadgesFromDb(badgeObj);
        return;
      }
      getCollectionFromDb({
        "collection": `games/${gameId}/gameElements`,
        "query1": {
          "key": "type",
          "condition": "==",
          "value": "badge"
        },
        "callback": callback,
        "handleListener": handleListener
      })
      function callback(data) {
        elementRef.current = {
          ...elementRef.current,
          ...{ [data.id]: data }
        }
        setBadgesFromDb(elementRef.current);
      }
      function handleListener(data) {
        elementListenerRef.current = data;
      }
    }
    getGameElementsToManage();
    return () => {
      if (elementListenerRef.current) {
        elementListenerRef.current();
        elementListenerRef.current = null;
      }
    }
  }, [gameId, isPreview, completeGameElementData])

  return (
    <div className='badges-landing'>

      <Routes>
        <Route path='' element={
          <div className='g-card badges-card'>
            <BackButton cancel={() => navigate(-1)} />
            <div className="g-space-1"></div>
            <div className='card-title'>
              <ImageContainer src='/images/icons/badges.png' alt='badges icon' className='head-icon' />
              Create and Edit Badges
            </div>
            <hr />
            <div className='badges-landing-content'>
              <BadgesList
                gameElements={gameElements}
                badgesFromDb={badgesFromDb}
                gameData={gameData}
                role={role} />
            </div>
          </div>
        }></Route>
        <Route path='create' element={
          <BadgesForm
            gameElements={gameElements}
            badgesFromDb={badgesFromDb}
            gameData={gameData}
            gameStats={gameStats}
            role={role} />
        }></Route>
        <Route path='edit/:badgePath' element={
          <BadgesForm
            gameElements={gameElements}
            badgesFromDb={badgesFromDb}
            gameData={gameData}
            gameStats={gameStats}
            role={role} />
        }></Route>
        <Route path="*" element={<Navigate replace to="/404" />} />
      </Routes>

      <UpgradeRequiredModal
        show={showUpgradeModal}
        cancel={() => setShowUpgradeModal(null)}
        message={showUpgradeModal}
      />
    </div>
  )
}

export default BadgesLandingPage